import React, { useState } from 'react'
import { Button, RadioButton, TextBox, Typography } from '@telavita-core/react-design-kit'
import { useDispatch } from 'react-redux'

import { PersonsService } from 'services/persons'

import { ReactComponent as MatchShowcaseIcon } from 'static/svg/match-showcase-icon.svg'
import { ReactComponent as TelavitaLogo } from 'static/svg/telavita-logo-grey.svg'
import { ReactComponent as CheckIcon } from 'static/svg/check-salmon.svg'

import ModalGeneric from '../ModalGeneric'

import { Loading } from './components/Loading'

import { questions as q } from './questions'

import * as S from './styles'

interface QuestionToMatch {
  title: string
  answer: string
  info: string
}

interface MatchProfessionalsModalProps {
  planGroupName: string
  onClose: () => void
  matchProfessionalHandled: () => void
}

export function MatchProfessionalsModal({
  onClose,
  planGroupName,
  matchProfessionalHandled,
}: MatchProfessionalsModalProps): JSX.Element {
  const [questions, setQuestions] = useState(q)
  const [actualPosition, setActualPosition] = useState(1)
  const [isFetching, setIsFetching] = useState(false)

  const dispatch = useDispatch()

  const actualQuestion = questions.find(q => q.position === actualPosition)
  const hasAnswerInActualQuestion = Boolean(actualQuestion?.answer)
  const hasOptionSelectedInActualQuestion = actualQuestion?.options?.some(o => o.isSelected)

  function updateAnswer(questionPosition: number, answer: string) {
    const selectedQuestion = questions.find(q => q.position === questionPosition)
    selectedQuestion.answer = answer

    const newQuestions = questions.filter(q => q.position !== questionPosition)
    newQuestions.push(selectedQuestion)
    
    setQuestions(newQuestions)
  }

  function markOptionInCheckbox(questionPosition: number, optionId: number) {
    const selectedQuestion = questions.find(q => q.position === questionPosition)
    selectedQuestion.options.forEach(o => {
      o.isSelected = false
    })

    const selectedOption = selectedQuestion.options.find(o => o.id === optionId)
    selectedOption.isSelected = true

    const newQuestions = questions.filter(q => q.position !== questionPosition)
    newQuestions.push(selectedQuestion)
    
    setQuestions(newQuestions)
  }

  function cleanAllAnswers() {
    questions.forEach(q => {
      q.answer = ''
      q?.options?.forEach(o => {
        o.isSelected = false
      })
    })
  }

  function goToNextQuestion() {
    setActualPosition(state => state + 1)
  }

  function goToBackQuestion() {
    setActualPosition(state => state - 1)
  }

  function formatQuestionsToMatch(questions: typeof q) {
    const questionsToMatch: QuestionToMatch[] = []

    const questionsWithAnswers = questions
      .filter(q => q?.answer)
      .map(q => ({ title: q.title, answer: q.answer, info: 'descritivo' }))

    const questionsWithOptions = questions
      .filter(q => !q?.answer && q?.options?.length)
      .map(q => 
        ({
          title: q.title,
          answer: q.options.find(q => q.isSelected).text,
          info: q.options.find(q => q.isSelected).info,
        }))
    
    questionsToMatch.push(...questionsWithAnswers)
    questionsToMatch.push(...questionsWithOptions)
    
    return questionsToMatch
  }

  async function handleFinishMatch() {
    try {
      setIsFetching(true)

      const questionsToMatch = formatQuestionsToMatch(questions)
      const { professionals, pagination } = await PersonsService.matchingProfessional({
        planGroupName,
        questions: questionsToMatch,
      })

      dispatch({
        type: 'SET_PAGINATION',
        payload: pagination,
      })

      dispatch({
        type: 'UPDATE_HOMEPAGE',
        payload: {
          persons: professionals,
        },
      })
    } catch {
      dispatch({
        type: 'SET_PAGINATION',
        payload: {},
      })

      dispatch({
        type: 'UPDATE_HOMEPAGE',
        payload: {
          persons: [],
        },
      })
    } finally {
      setIsFetching(false)
      matchProfessionalHandled()
      cleanAllAnswers()
      onClose()
    }
  }

  return (
    <ModalGeneric
      modalTitle={
        isFetching
          ? null
          : (
            <S.MatchProfessionalsTitle>
              <MatchShowcaseIcon width={130} height={49} />
              <div>
                <Typography variant='content3'>DESCUBRA O SEU PSICÓLOGO IDEAL</Typography>
                <TelavitaLogo />
              </div>
            </S.MatchProfessionalsTitle>
          )
      }
      onCloseModal={() => {
        cleanAllAnswers()
        onClose()
      }}
      hasCloseButton={!isFetching}
      type='large'
    >
      <S.ModalContainer>
        {isFetching && <Loading />}

        {!isFetching && questions.map(question => {
          const firstQuestion = actualPosition === 1
          const lastQuestion = actualPosition === questions.length
          const hasPreviousButton = !firstQuestion

          if (actualPosition !== question.position) return

          return (
            <S.MatchProfessionalsContainer key={question.position}>
              <S.StepsContainer>
                <Typography
                  variant='content3'
                  weight='bold'
                >
                  PASSO {question.position} DE {questions.length}
                </Typography>
              </S.StepsContainer>

              <S.QuestionContainer>
                <Typography
                  variant='header3'
                  weight='bolder'
                  center
                >
                  {question.title}
                </Typography>

                {question.type === 'textarea' && (
                  <TextBox
                    placeholder={question.placeholder ?? ''}
                    value={question.answer ?? ''}
                    onChange={(event) => {
                      updateAnswer(question.position, event.target.value)
                    }}
                  />
                )}

                {question.type === 'checkbox' && (
                  <S.RadioButtonContainer>
                    {question.options.map(option =>
                      <S.PlaceholderRadioButton
                        key={option.id}
                        isSelected={option.isSelected}
                      >
                        <RadioButton 
                          isSelected={option.isSelected} 
                          onSelect={() => {
                            markOptionInCheckbox(question.position, option.id)
                          }}
                        >
                          <S.RadioContentWrapper>
                            <S.RadioButtonTextWrapper>
                              <Typography variant='content2'>
                                {option.text}
                              </Typography>
                            </S.RadioButtonTextWrapper>
                            {option.isSelected ? <CheckIcon /> : <S.EmptyIcon />}
                          </S.RadioContentWrapper>
                        </RadioButton>
                      </S.PlaceholderRadioButton>
                    )}
                  </S.RadioButtonContainer>
                )}

                <S.ComplementaryInfoContainer>
                  {question?.complementaryInfo && question.complementaryInfo}
                </S.ComplementaryInfoContainer>
              </S.QuestionContainer>

              <S.Footer>
                {hasPreviousButton && (
                  <Button
                    variant='outlined'
                    onClick={() => {
                      goToBackQuestion()
                    }}
                  >
                    VOLTAR
                  </Button>
                )}

                <Button
                  variant='contained'
                  disabled={!hasAnswerInActualQuestion && !hasOptionSelectedInActualQuestion}
                  onClick={() => {
                    if (lastQuestion) {
                      handleFinishMatch()
                    } else {
                      goToNextQuestion()
                    }
                  }}
                >
                  {lastQuestion ? 'CONCLUIR' : 'AVANÇAR'}
                </Button>
              </S.Footer>
            </S.MatchProfessionalsContainer>
          )
        })}
      </S.ModalContainer>
    </ModalGeneric>
  )
}